import { Button as BaseButton } from "@base-ui/react/button"
import { recipe, splitRecipeProps, type RecipeVariantProps } from "dx-styles"
import type { ReactElement } from "react"

import { bodyTextStyle, tokens } from "../theme"
import type { DistributiveOmit } from "../types"

/**
 * Hover and active shades are derived with `color-mix` rather than added as
 * tokens, which keeps the contract to the colours the design system actually
 * names.
 */
const darken = (color: string, amount: string) =>
  `color-mix(in oklab, ${color}, black ${amount})`

export const buttonRecipe = recipe({
  base: {
    ...bodyTextStyle,
    fontWeight: tokens.fontWeight.semibold,
    alignItems: "center",
    justifyContent: "center",
    display: "inline-flex",
    gap: tokens.space.sm,
    height: tokens.size.sm,
    paddingInline: tokens.space.md,
    borderRadius: tokens.radius.sm,
    border: "1px solid transparent",
    cursor: "pointer",
    whiteSpace: "nowrap",
    // `render={<a />}` is a supported Base UI target, so the anchor defaults
    // have to be cleared here rather than by the consumer.
    textDecoration: "none",
    transition: `background-color ${tokens.duration.fast}, border-color ${tokens.duration.fast}, color ${tokens.duration.fast}`,
    "&:focus-visible": {
      outline: `2px solid ${tokens.color.primary}`,
      outlineOffset: "2px",
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: tokens.opacity.muted,
    },
  },
  variants: {
    variant: {
      primary: {
        backgroundColor: tokens.color.primary,
        color: tokens.color.textOnDark,
        "&:not(:disabled):hover": {
          backgroundColor: darken(tokens.color.primary, "10%"),
        },
        "&:not(:disabled):active": {
          backgroundColor: darken(tokens.color.primary, "18%"),
        },
      },
      secondary: {
        backgroundColor: tokens.color.secondary,
        color: tokens.color.text,
        "&:not(:disabled):hover": {
          backgroundColor: darken(tokens.color.secondary, "6%"),
        },
        "&:not(:disabled):active": {
          backgroundColor: darken(tokens.color.secondary, "12%"),
        },
      },
      ghost: {
        backgroundColor: "transparent",
        color: tokens.color.text,
        "&:not(:disabled):hover": {
          backgroundColor: tokens.color.hover,
        },
        "&:not(:disabled):active": {
          backgroundColor: darken(tokens.color.hover, "6%"),
        },
      },
      danger: {
        backgroundColor: tokens.color.error,
        color: tokens.color.textOnDark,
        "&:not(:disabled):hover": {
          backgroundColor: darken(tokens.color.error, "10%"),
        },
        "&:not(:disabled):active": {
          backgroundColor: darken(tokens.color.error, "18%"),
        },
      },
      /**
       * Square, icon-only. Listed as a variant rather than its own component so
       * the whole button surface stays one axis — the trade-off is that an icon
       * button cannot also be `primary` or `danger`.
       */
      iconButton: {
        backgroundColor: "transparent",
        color: tokens.color.text,
        width: tokens.size.sm,
        paddingInline: tokens.space.none,
        "&:not(:disabled):hover": {
          backgroundColor: tokens.color.hover,
        },
        "&:not(:disabled):active": {
          backgroundColor: darken(tokens.color.hover, "6%"),
        },
      },
    },
  },
  defaultVariants: {
    variant: "secondary",
  },
})

type ButtonVariantProps = RecipeVariantProps<typeof buttonRecipe>

export type ButtonProps = ButtonVariantProps &
  DistributiveOmit<BaseButton.Props, keyof ButtonVariantProps | "className">

/**
 * Wraps Base UI's `Button`, which supplies the accessible button behaviour
 * (disabled handling, `focusableWhenDisabled`, and non-native `render` targets).
 */
export const Button = (props: ButtonProps): ReactElement => {
  const { otherProps, variantProps } = splitRecipeProps(buttonRecipe, props)

  return (
    <BaseButton
      {...otherProps}
      className={buttonRecipe(variantProps)}
    />
  )
}
